/**
 * EditableNumberField Component
 *
 * Ett fält för att visa och redigera numeriska värden.
 * Visar visuell indikator om värdet har ändrats från originalvärdet.
 */

import { useState, useEffect, useRef } from "react";
import { RotateCcw, Pencil } from "lucide-react";
import { formatNumber, parseFormattedNumber } from "@/lib/format";

interface EditableNumberFieldProps {
  value: number;
  originalValue: number;
  onChange: (value: number) => void;
  onReset: () => void;
  label?: string;
  className?: string;
  isNegative?: boolean; // Om värdet ska visas som negativt (t.ex. kostnader)
  disabled?: boolean;
  small?: boolean; // Smaller font size for compact displays
}

export function EditableNumberField({
  value,
  originalValue,
  onChange,
  onReset,
  className = "",
  isNegative = false,
  disabled = false,
  small = false,
}: EditableNumberFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const isModified = value !== originalValue;
  const displayValue = isNegative && value > 0 ? -value : value;

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleStartEdit = () => {
    if (disabled) return;
    setInputValue(formatNumber(Math.abs(value)));
    setIsEditing(true);
  };

  const handleBlur = () => {
    const parsed = parseFormattedNumber(inputValue);
    onChange(parsed);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBlur();
    } else if (e.key === "Escape") {
      setIsEditing(false);
    }
  };

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    onReset();
  };

  if (isEditing) {
    return (
      <div className={`relative ${className}`}>
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`w-full text-right font-medium px-2 py-1 border border-primary-400 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white ${
            small ? "text-xs" : ""
          }`}
        />
      </div>
    );
  }

  return (
    <div
      className={`group relative flex items-center justify-end ${className} ${
        !disabled
          ? "cursor-pointer hover:bg-primary-50 rounded px-1.5 py-0.5 -mx-1 border border-dashed border-transparent hover:border-primary-300 transition-all"
          : ""
      }`}
      onClick={handleStartEdit}
      title={!disabled ? "Klicka för att redigera" : undefined}
    >
      {/* Modified indicator */}
      {isModified && (
        <button
          onClick={handleReset}
          className="absolute -left-6 opacity-0 group-hover:opacity-100 transition-opacity p-1 text-gray-400 hover:text-primary-600"
          title={`Återställ till ${formatNumber(originalValue)}`}
        >
          <RotateCcw size={small ? 12 : 14} />
        </button>
      )}

      {/* Value */}
      <span
        className={`font-medium tabular-nums ${small ? "text-xs" : ""} ${
          isModified ? "text-primary-700 bg-primary-50 px-1 rounded" : ""
        } ${displayValue < 0 ? "text-red-600" : ""}`}
      >
        {formatNumber(displayValue)}
      </span>

      {/* Edit indicator - now always visible with subtle opacity */}
      {!disabled && (
        <Pencil
          size={small ? 10 : 12}
          className="ml-1.5 text-primary-400 opacity-40 group-hover:opacity-100 transition-opacity"
        />
      )}
    </div>
  );
}

/**
 * ReadOnlyNumberField - För visning av beräknade summeringar som inte kan redigeras
 */
interface ReadOnlyNumberFieldProps {
  value: number;
  originalValue?: number;
  className?: string;
  bold?: boolean;
  highlight?: boolean;
  small?: boolean;
}

export function ReadOnlyNumberField({
  value,
  originalValue,
  className = "",
  bold = false,
  highlight = false,
  small = false,
}: ReadOnlyNumberFieldProps) {
  const isModified = originalValue !== undefined && value !== originalValue;

  return (
    <span
      className={`tabular-nums ${small ? "text-xs" : ""} ${bold ? "font-bold" : "font-medium"} ${
        highlight ? "text-primary-900" : ""
      } ${isModified ? "text-primary-700 bg-primary-50 px-1 rounded" : ""} ${
        value < 0 ? "text-red-600" : ""
      } ${className}`}
    >
      {formatNumber(value)}
    </span>
  );
}
